import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./CalendarDatePicker.css";

const toDateValue = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const formatDateLabel = (value) => {
  if (!value) return "";
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-ZA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const CalendarDatePicker = ({ id, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
  const fieldRef = useRef(null);
  const calendarRef = useRef(null);
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const initialDate = value ? new Date(`${value}T00:00:00`) : new Date();
    return new Date(initialDate.getFullYear(), initialDate.getMonth(), 1);
  });
  const monthStart = new Date(
    visibleMonth.getFullYear(),
    visibleMonth.getMonth(),
    1,
  );
  const leadingDays = monthStart.getDay();
  const daysInMonth = new Date(
    monthStart.getFullYear(),
    monthStart.getMonth() + 1,
    0,
  ).getDate();

  const changeMonth = (offset) => {
    setVisibleMonth(
      (month) => new Date(month.getFullYear(), month.getMonth() + offset, 1),
    );
  };

  const selectDate = (date) => {
    onChange(toDateValue(date));
    setIsOpen(false);
  };

  useLayoutEffect(() => {
    if (!isOpen) return undefined;

    const updatePosition = () => {
      const field = fieldRef.current;
      const calendar = calendarRef.current;
      if (!field || !calendar) return;

      const fieldRect = field.getBoundingClientRect();
      const calendarRect = calendar.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth;
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const horizontalPadding = 8;
      const left = Math.min(
        Math.max(horizontalPadding, fieldRect.left),
        viewportWidth - calendarRect.width - horizontalPadding,
      );
      const spaceBelow = viewportHeight - fieldRect.bottom - horizontalPadding;
      const spaceAbove = fieldRect.top - horizontalPadding;
      const desiredTop =
        spaceBelow >= calendarRect.height || spaceBelow >= spaceAbove
          ? fieldRect.bottom + horizontalPadding
          : Math.max(
              horizontalPadding,
              fieldRect.top - calendarRect.height - horizontalPadding,
            );
      const top = Math.min(
        desiredTop,
        viewportHeight - calendarRect.height - horizontalPadding,
      );

      setCalendarPosition((current) =>
        current.top === top && current.left === left
          ? current
          : { top, left },
      );
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    window.visualViewport?.addEventListener("resize", updatePosition);
    window.visualViewport?.addEventListener("scroll", updatePosition);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      window.visualViewport?.removeEventListener("resize", updatePosition);
      window.visualViewport?.removeEventListener("scroll", updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const closeOnOutsideClick = (event) => {
      if (
        !fieldRef.current?.contains(event.target) &&
        !calendarRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isOpen]);

  return (
    <div className="profile-date-field" ref={fieldRef}>
      <svg
        className="profile-date-icon"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M4 10h16m-8-3V4M7 7V4m10 3V4M5 20h14a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1Zm3-7h.01M12 13h.01M16 13h.01M8 17h.01M12 17h.01M16 17h.01"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.8"
        />
      </svg>
      <input
        id={id}
        className="profile-date-input"
        type="text"
        value={formatDateLabel(value)}
        placeholder="Select date"
        readOnly
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={`${id}-calendar`}
        onClick={() => setIsOpen((open) => !open)}
      />
      <button
        type="button"
        className="profile-date-toggle"
        aria-label={isOpen ? "Close calendar" : "Open calendar"}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        <svg viewBox="0 0 20 20" aria-hidden="true">
          <path d="m5 7.5 5 5 5-5" />
        </svg>
      </button>
      {isOpen &&
        createPortal(
          <div
            className="profile-calendar"
            id={`${id}-calendar`}
            ref={calendarRef}
            role="dialog"
            aria-label="Choose a launch date"
            style={calendarPosition}
          >
            <div className="profile-calendar-header">
              <button
                type="button"
                aria-label="Previous month"
                onClick={() => changeMonth(-1)}
              >
                <svg viewBox="0 0 20 20" aria-hidden="true">
                  <path d="m12 4-6 6 6 6" />
                </svg>
              </button>
              <strong>
                {monthStart.toLocaleDateString("en", {
                  month: "long",
                  year: "numeric",
                })}
              </strong>
              <button
                type="button"
                aria-label="Next month"
                onClick={() => changeMonth(1)}
              >
                <svg viewBox="0 0 20 20" aria-hidden="true">
                  <path d="m8 4 6 6-6 6" />
                </svg>
              </button>
            </div>
            <div className="profile-calendar-grid" role="grid">
              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                <span
                  className="profile-calendar-weekday"
                  key={day}
                  role="columnheader"
                >
                  {day}
                </span>
              ))}
              {Array.from({ length: leadingDays }, (_, index) => (
                <span
                  className="profile-calendar-empty"
                  key={`empty-${index}`}
                  aria-hidden="true"
                />
              ))}
              {Array.from({ length: daysInMonth }, (_, index) => {
                const date = new Date(
                  monthStart.getFullYear(),
                  monthStart.getMonth(),
                  index + 1,
                );
                const dateValue = toDateValue(date);
                const isSelected = dateValue === value;

                return (
                  <button
                    type="button"
                    className={[
                      "profile-calendar-day",
                      isSelected ? "selected" : "",
                      dateValue === toDateValue(new Date()) ? "today" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    key={dateValue}
                    aria-label={date.toLocaleDateString("en-ZA", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                    aria-pressed={isSelected}
                    onClick={() => selectDate(date)}
                  >
                    {index + 1}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="profile-calendar-today"
              onClick={() => selectDate(new Date())}
            >
              Today
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default CalendarDatePicker;
